import { useEffect, useRef, useState } from 'react';
import { api, avatarUrl } from '../api.js';
import { useGuild } from './GuildLayout.jsx';
import { renderPreview, CARD_DIMS } from '../lib/cardPreview.js';
import ConfigAutoRole from './ConfigAutoRole.jsx';

function renderWelcomeText(template, ctx) {
  if (!template) return null;
  return template
    .replaceAll('{user}', `__MENTION_${ctx.username}__`)
    .replaceAll('{username}', ctx.username)
    .replaceAll('{tag}', `${ctx.username}#0`)
    .replaceAll('{server}', ctx.server)
    .replaceAll('{memberCount}', String(ctx.memberCount));
}

const FONTS = [
  { value: 'sans-serif', label: 'Sans-serif (default)' },
  { value: 'serif', label: 'Serif' },
  { value: 'monospace', label: 'Monospace' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet' },
  { value: '"Courier New", monospace', label: 'Courier' },
  { value: '"Georgia", serif', label: 'Georgia' },
  { value: '"Impact", sans-serif', label: 'Impact' },
];

export default function ConfigWelcome() {
  const { guildId, guild, channels } = useGuild();
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [bgUrl, setBgUrl] = useState(null);
  const [dragging, setDragging] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    api.getConfig(guildId).then(c => {
      setCfg({
        welcome_channel_id:          c.welcome_channel_id ?? null,
        welcome_message:             c.welcome_message ?? '',
        welcome_card_enabled:        !!c.welcome_card_enabled,
        welcome_card_bg_filename:    c.welcome_card_bg_filename ?? null,
        welcome_card_title:          c.welcome_card_title ?? 'Welcome {username}!',
        welcome_card_subtitle:       c.welcome_card_subtitle ?? 'You are member #{memberCount}',
        welcome_card_font_family:    c.welcome_card_font_family ?? 'sans-serif',
        welcome_card_title_size:     c.welcome_card_title_size ?? 54,
        welcome_card_title_color:    c.welcome_card_title_color ?? '#ffffff',
        welcome_card_subtitle_size:  c.welcome_card_subtitle_size ?? 24,
        welcome_card_subtitle_color: c.welcome_card_subtitle_color ?? '#dddddd',
        welcome_card_bg_scale:       c.welcome_card_bg_scale ?? 1.0,
        welcome_card_bg_offset_x:    c.welcome_card_bg_offset_x ?? 0,
        welcome_card_bg_offset_y:    c.welcome_card_bg_offset_y ?? 0,
        welcome_card_bg_darken:      c.welcome_card_bg_darken ?? 0.35,
      });
      setBgUrl(c.welcome_card_bg_filename ? api.welcomeBgUrl(guildId) : null);
      setDirty(false);
    });
  }, [guildId]);

  // Re-render preview whenever card config changes
  useEffect(() => {
    if (!cfg?.welcome_card_enabled || !canvasRef.current) return;
    const previewCfg = {
      bgUrl,
      title: cfg.welcome_card_title,
      subtitle: cfg.welcome_card_subtitle,
      font_family: cfg.welcome_card_font_family,
      title_size: cfg.welcome_card_title_size,
      title_color: cfg.welcome_card_title_color,
      subtitle_size: cfg.welcome_card_subtitle_size,
      subtitle_color: cfg.welcome_card_subtitle_color,
      bg_scale: cfg.welcome_card_bg_scale,
      bg_offset_x: cfg.welcome_card_bg_offset_x,
      bg_offset_y: cfg.welcome_card_bg_offset_y,
      bg_darken: cfg.welcome_card_bg_darken,
    };
    const previewCtx = {
      username: 'NewMember',
      tag: 'NewMember#0',
      server: guild?.name || 'Server',
      memberCount: (guild?.member_count || 0) + 1,
      avatarUrl: 'https://cdn.discordapp.com/embed/avatars/3.png',
    };
    renderPreview(canvasRef.current, previewCfg, previewCtx);
  }, [cfg, bgUrl, guild]);

  function onDragStart(e) {
    if (!bgUrl) return;
    setDragging({
      startX: e.clientX,
      startY: e.clientY,
      initialX: cfg.welcome_card_bg_offset_x,
      initialY: cfg.welcome_card_bg_offset_y,
    });
  }

  function onDragMove(e) {
    if (!dragging || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CARD_DIMS.W / rect.width;
    const scaleY = CARD_DIMS.H / rect.height;
    const dx = (e.clientX - dragging.startX) * scaleX;
    const dy = (e.clientY - dragging.startY) * scaleY;
    setCfg(c => ({
      ...c,
      welcome_card_bg_offset_x: Math.round(dragging.initialX + dx),
      welcome_card_bg_offset_y: Math.round(dragging.initialY + dy),
    }));
    setDirty(true);
  }

  function onDragEnd() {
    setDragging(null);
  }

  function resetBgTransform() {
    setCfg(c => ({ ...c, welcome_card_bg_scale: 1.0, welcome_card_bg_offset_x: 0, welcome_card_bg_offset_y: 0, welcome_card_bg_darken: 0.35 }));
    setDirty(true);
  }

  if (!cfg) return (
    <div className="card">
      <div className="skel skel-line short" />
      <div className="skel" style={{ height: 38, marginTop: 12 }} />
      <div className="skel" style={{ height: 80, marginTop: 16 }} />
    </div>
  );

  function update(field, value) {
    setCfg(c => ({ ...c, [field]: value }));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.putConfig(guildId, {
        ...cfg,
        welcome_card_enabled: cfg.welcome_card_enabled ? 1 : 0,
      });
      setDirty(false);
      setToast({ type: 'success', msg: 'Saved' });
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function uploadBg(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { filename } = await api.uploadWelcomeBg(guildId, file);
      update('welcome_card_bg_filename', filename);
      setBgUrl(api.welcomeBgUrl(guildId));
      setToast({ type: 'success', msg: 'Background uploaded' });
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function removeBg() {
    try {
      await api.deleteWelcomeBg(guildId);
      update('welcome_card_bg_filename', null);
      setBgUrl(null);
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    }
  }

  const previewRendered = renderWelcomeText(cfg.welcome_message, {
    username: 'NewMember',
    server: guild?.name || 'this server',
    memberCount: (guild?.member_count || 0) + 1,
  });

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Welcome</h2>
      <p className="muted" style={{ marginTop: '-.25rem' }}>Greet new members with a text message, an image card, or both.</p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Channel</h3>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Welcome channel</label>
          <select value={cfg.welcome_channel_id || ''} onChange={e => update('welcome_channel_id', e.target.value || null)}>
            <option value="">— disabled —</option>
            {channels.filter(c => c.type === 0 || c.type === 5).map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Text message</h3>
        <p className="muted" style={{ marginTop: '-.5rem', fontSize: 13 }}>Posted alongside the card (if enabled). Leave blank to skip.</p>
        <div className="field">
          <label>Message</label>
          <textarea
            rows={3}
            value={cfg.welcome_message || ''}
            onChange={e => update('welcome_message', e.target.value)}
            placeholder="Welcome {user} to {server}!"
          />
          <p className="dim" style={{ fontSize: 12, marginTop: 6 }}>
            Placeholders: <span className="tag">{'{user}'}</span> mention,{' '}
            <span className="tag">{'{username}'}</span>, <span className="tag">{'{tag}'}</span>,{' '}
            <span className="tag">{'{server}'}</span>, <span className="tag">{'{memberCount}'}</span>.
          </p>
        </div>
        {cfg.welcome_message && (
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Preview</label>
            <div className="welcome-preview">
              <div className="header">
                <img className="avatar" src="/soupos-icon.png" alt="" style={{ width: 22, height: 22 }} />
                <span className="name">SoupOS</span>
                <span className="botbadge">BOT</span>
                <span className="when">today</span>
              </div>
              <div className="body">{previewRendered?.split(/(__MENTION_[A-Za-z0-9_]+__)/g).map((chunk, i) => {
                const m = chunk.match(/^__MENTION_([A-Za-z0-9_]+)__$/);
                return m ? <span key={i} className="mention">@{m[1]}</span> : <span key={i}>{chunk}</span>;
              })}</div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="row" style={{ alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0 }}>Welcome card</h3>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Generated image attached to the welcome message. Live preview updates as you edit.</p>
          </div>
          <label className="event-toggle" style={{ flex: 0, padding: '.5rem .8rem' }}>
            <input type="checkbox" checked={cfg.welcome_card_enabled} onChange={e => update('welcome_card_enabled', e.target.checked)} />
            <span>Enable card</span>
          </label>
        </div>

        {cfg.welcome_card_enabled && (
          <>
            <div style={{ marginBottom: '1.25rem' }}>
              <canvas
                ref={canvasRef}
                width={CARD_DIMS.W}
                height={CARD_DIMS.H}
                onMouseDown={onDragStart}
                onMouseMove={onDragMove}
                onMouseUp={onDragEnd}
                onMouseLeave={onDragEnd}
                style={{
                  width: '100%', height: 'auto', borderRadius: 8,
                  background: '#1b1d2c', border: '1px solid var(--border)',
                  cursor: bgUrl ? (dragging ? 'grabbing' : 'grab') : 'default',
                  userSelect: 'none',
                }}
              />
              {bgUrl && <p className="dim" style={{ fontSize: 12, marginTop: 6 }}>Drag the preview to reposition the background.</p>}
            </div>

            <div className="field">
              <label>Background image</label>
              <div className="row" style={{ flex: 0, gap: '.5rem' }}>
                <label className="btn ghost" style={{ cursor: 'pointer' }}>
                  Upload image
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={uploadBg} style={{ display: 'none' }} />
                </label>
                {cfg.welcome_card_bg_filename && (
                  <button type="button" className="ghost" onClick={removeBg}>Remove</button>
                )}
                <span className="dim" style={{ fontSize: 12 }}>
                  {cfg.welcome_card_bg_filename ? 'Custom background uploaded' : 'Using default gradient'} · max 5 MB · PNG/JPG/WebP/GIF
                </span>
              </div>
            </div>

            {cfg.welcome_card_bg_filename && (
              <>
                <div className="field">
                  <label>Background zoom <span className="dim" style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 400 }}>· {cfg.welcome_card_bg_scale.toFixed(2)}x</span></label>
                  <input
                    type="range" min="1" max="4" step="0.05"
                    value={cfg.welcome_card_bg_scale}
                    onChange={e => update('welcome_card_bg_scale', Number(e.target.value))}
                    style={{ flex: 1, padding: 0, background: 'transparent', border: 'none', accentColor: 'var(--accent)' }}
                  />
                </div>
                <div className="field">
                  <label>Darken overlay <span className="dim" style={{ textTransform: 'none', letterSpacing: 'normal', fontWeight: 400 }}>· {Math.round(cfg.welcome_card_bg_darken * 100)}%</span></label>
                  <div className="row" style={{ gap: '.75rem', alignItems: 'center' }}>
                    <input
                      type="range" min="0" max="1" step="0.05"
                      value={cfg.welcome_card_bg_darken}
                      onChange={e => update('welcome_card_bg_darken', Number(e.target.value))}
                      style={{ flex: 1, padding: 0, background: 'transparent', border: 'none', accentColor: 'var(--accent)' }}
                    />
                    <button type="button" className="ghost" onClick={resetBgTransform}>Reset</button>
                  </div>
                  <p className="dim" style={{ fontSize: 12, marginTop: 4 }}>Black overlay that helps text stand out. 0% shows the raw image; 100% is a black card.</p>
                </div>
              </>
            )}

            <div className="field">
              <label>Title</label>
              <input value={cfg.welcome_card_title || ''} onChange={e => update('welcome_card_title', e.target.value)} />
            </div>
            <div className="field">
              <label>Subtitle</label>
              <input value={cfg.welcome_card_subtitle || ''} onChange={e => update('welcome_card_subtitle', e.target.value)} />
            </div>

            <div className="field">
              <label>Font family</label>
              <select value={cfg.welcome_card_font_family} onChange={e => update('welcome_card_font_family', e.target.value)}>
                {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>

            <div className="row" style={{ gap: '1rem' }}>
              <div className="field">
                <label>Title size (px)</label>
                <input type="number" min="20" max="100" value={cfg.welcome_card_title_size} onChange={e => update('welcome_card_title_size', Number(e.target.value))} />
              </div>
              <div className="field">
                <label>Title color</label>
                <div style={{ display: 'flex', gap: '.5rem' }}>
                  <input type="color" value={cfg.welcome_card_title_color} onChange={e => update('welcome_card_title_color', e.target.value)} style={{ width: 60, padding: 4 }} />
                  <input value={cfg.welcome_card_title_color} onChange={e => update('welcome_card_title_color', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="row" style={{ gap: '1rem' }}>
              <div className="field">
                <label>Subtitle size (px)</label>
                <input type="number" min="12" max="60" value={cfg.welcome_card_subtitle_size} onChange={e => update('welcome_card_subtitle_size', Number(e.target.value))} />
              </div>
              <div className="field">
                <label>Subtitle color</label>
                <div style={{ display: 'flex', gap: '.5rem' }}>
                  <input type="color" value={cfg.welcome_card_subtitle_color} onChange={e => update('welcome_card_subtitle_color', e.target.value)} style={{ width: 60, padding: 4 }} />
                  <input value={cfg.welcome_card_subtitle_color} onChange={e => update('welcome_card_subtitle_color', e.target.value)} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="row actions">
        <button disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>

      <ConfigAutoRole />

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
